import { Link } from 'react-router-dom';
import { ArrowRight, HandHeart, Users, ShieldCheck, Sparkle, Certificate } from '@phosphor-icons/react';
import { Reveal } from '@/components/motion';
import { IMAGES, VALUES, TRUST_POINTS } from '@/data/site';
import IconResolver from '@/components/IconResolver';
import { useContent } from '@/context/ContentContext';

export default function About() {
  const c = useContent();
  return (
    <>
      <section className="pt-16 md:pt-24 pb-16" data-testid="about-hero">
        <div className="container-x">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-7">
              <Reveal>
                <div className="overline mb-6">About Inclusive Mobility Group</div>
                <h1 className="h-display text-balance">
                  Australia deserves transport built around people, not around routes.
                </h1>
              </Reveal>
              <Reveal delay={0.15}>
                <p className="body-lg mt-8 max-w-2xl text-pretty">
                  Inclusive Mobility Group is a national community transport provider — NDIS registration in progress — delivering community,
                  disability, aged care, medical and hospital discharge transport in every Australian
                  state and territory. We were built by people who spent decades in health and
                  disability services — and we've engineered the operating model we always wished existed.
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <Reveal delay={0.1}>
                <div className="rounded-3xl overflow-hidden aspect-[4/5] bg-neutral-100">
                  <img src={IMAGES.senior_outside} alt="Older Australians outdoors" className="w-full h-full object-cover" />
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24 bg-neutral-50" data-testid="about-mission">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-14 items-start">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="overline mb-5">Our mission</div>
              <h2 className="h-section text-balance" data-testid="about-mission-text">
                {c('about.mission', 'Safe, timely, respectful transport for every Australian — regardless of ability, funding or postcode.')}
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 space-y-5">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08}>
                <div className="p-7 rounded-2xl bg-white border border-neutral-200">
                  <h3 className="font-display font-bold text-xl text-ink">{v.title}</h3>
                  <p className="body-md mt-2">{v.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24" data-testid="about-standards">
        <div className="container-x">
          <div className="max-w-3xl mb-14">
            <Reveal>
              <div className="overline mb-5">Our operating standard</div>
              <h2 className="h-section text-balance">Six things we do differently. Every trip. Every driver. Every day.</h2>
            </Reveal>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {TRUST_POINTS.map((tp, i) => (
              <Reveal key={tp.title} delay={i * 0.06}>
                <div className="p-7 rounded-3xl bg-neutral-50 border border-neutral-200 h-full">
                  <div className="w-11 h-11 rounded-xl bg-brand/10 flex items-center justify-center mb-4">
                    <IconResolver name={tp.icon} size={22} className="text-brand" />
                  </div>
                  <h3 className="font-display font-bold text-lg text-ink">{tp.title}</h3>
                  <p className="body-md mt-2">{tp.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24 bg-ink text-white">
        <div className="container-x">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-12">
            <div className="lg:col-span-7">
              <Reveal>
                <div className="overline text-brand-soft mb-5">Our people</div>
                <h2 className="h-section text-white text-balance">
                  A national team. A single standard.
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-4 lg:col-start-9">
              <Reveal delay={0.15}>
                <p className="text-white/70 body-md">
                  Our drivers, dispatchers and coordinators come from health, aged care, disability support and community
                  service backgrounds. Care isn't a training module — it's the reason they're here.
                </p>
              </Reveal>
            </div>
          </div>
          <Reveal>
            <div className="rounded-3xl overflow-hidden aspect-[16/8] bg-white/5">
              <img src={IMAGES.team_meeting} alt="Inclusive Mobility Group team" className="w-full h-full object-cover opacity-90" />
            </div>
          </Reveal>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Link to="/careers" className="btn-primary">Join the team <ArrowRight size={16} weight="bold" /></Link>
            <Link to="/contact" className="text-white/80 hover:text-white btn-ghost">Contact leadership</Link>
          </div>
        </div>
      </section>
    </>
  );
}
